import { createSlice, isAnyOf } from '@reduxjs/toolkit';
import type { AppState } from '../../reducers/root.reducer';
import { createSelector } from 'reselect';
import IPaginationState from '../../types/state/IPaginationState';
import ProjectParticipant from '../types/project/ProjectParticipant';
import AdminParticipantService from '../services/AdminParticipantService';
import ImportService from '../services/ImportService';
import ImportProcess from '../../common/types/ImportProcess';
import ImportStates, { ImportState } from '../constants/ImportStates';
import type { ParticipantImportPreviewResponse } from '../types/ParticipantImportPreviewResponse';
import { LOGOUT } from '../../constants/actions/auth.constants';
import { createNotifiedThunk } from '../../reducers/createAppAsyncThunk';

const NAME = 'AdminParticipant';

const getParticipants = createNotifiedThunk(
    `${NAME}/getParticipants`,
    AdminParticipantService.getParticipants,
    { error: 'fe.admin.error.load-participants' },
);
const addParticipant = createNotifiedThunk(
    `${NAME}/addParticipant`,
    AdminParticipantService.addParticipant,
    { error: 'fe.admin.error.add-participant' },
);
const deleteParticipant = createNotifiedThunk(
    `${NAME}/deleteParticipant`,
    AdminParticipantService.deleteParticipant,
    { error: 'fe.admin.error.delete-participant' },
);
const batchDeleteParticipants = createNotifiedThunk(
    `${NAME}/batchDeleteParticipants`,
    AdminParticipantService.batchDeleteParticipants,
    {
        error: 'fe.admin.error.batch-delete-participants',
        success: 'fe.admin.success.batch-delete-participants',
    },
);
const deleteAllParticipants = createNotifiedThunk(
    `${NAME}/deleteAllParticipants`,
    AdminParticipantService.deleteAllParticipants,
    {
        error: 'fe.admin.error.delete-all-participants',
        success: 'fe.admin.success.delete-all-participants',
    },
);
const updateCriterionValues = createNotifiedThunk(
    `${NAME}/updateCriterionValues`,
    AdminParticipantService.updateCriterionValues,
    { error: 'fe.admin.error.update-values' },
);
const importParticipants = createNotifiedThunk(
    `${NAME}/importParticipants`,
    AdminParticipantService.importParticipants,
    { error: 'fe.admin.error.import-participants' },
);
const confirmImport = createNotifiedThunk(
    `${NAME}/confirmImport`,
    ImportService.confirmImport,
    { error: 'fe.admin.error.import-file' },
);
const cancelImport = createNotifiedThunk(
    `${NAME}/cancelImport`,
    ImportService.cancelImport,
    { error: 'fe.admin.error.cancel-import' },
);
const getImportProgress = createNotifiedThunk(
    `${NAME}/getImportProgress`,
    ImportService.getImportProgress,
    { error: 'fe.admin.error.import-users' },
);
const exportParticipants = createNotifiedThunk(
    `${NAME}/exportParticipants`,
    AdminParticipantService.exportParticipants,
    { error: 'fe.admin.error.export-participants' },
);
const getImportTemplate = createNotifiedThunk(
    `${NAME}/getImportTemplate`,
    AdminParticipantService.getImportTemplate,
    { error: 'fe.admin.error.fetch-template' },
);
const getProjectParticipants = createNotifiedThunk(
    `${NAME}/getProjectParticipants`,
    AdminParticipantService.getParticipants,
    { error: 'fe.admin.error.load-participants' },
);

export type AdminParticipantState = {
    participants: IPaginationState<ProjectParticipant[] | null>;
    projectParticipants: IPaginationState<ProjectParticipant[]>;
    import: {
        loading: boolean;
        preview: ParticipantImportPreviewResponse | null;
        process: ImportProcess | null;
        state: ImportState;
        importId: number | null;
    };
};

const initialState: AdminParticipantState = {
    participants: { data: null, pagination: { page: 1 } },
    projectParticipants: { data: [], loading: false, pagination: { page: 1 } },
    import: {
        loading: false,
        preview: null,
        process: null,
        state: ImportStates.Idle,
        importId: ImportService.getImportId(),
    },
};

function resetImport(state: AdminParticipantState) {
    state.import.state = ImportStates.Idle;
    state.import.importId = null;
    state.import.preview = null;
    state.import.process = null;
}

const slice = createSlice({
    name: NAME,
    initialState,
    reducers: {
        resetImport: (state) => resetImport(state),
    },
    extraReducers: (builder) => {
        builder.addCase(
            getParticipants.fulfilled,
            (state: AdminParticipantState, action) => {
                state.participants.data = action.payload.data;
                const payload = action.payload as { pagination?: unknown };
                if (payload.pagination) {
                    state.participants.pagination = payload.pagination as {
                        page?: number;
                        nextPage?: number;
                        prevPage?: number;
                        pageCount?: number;
                    };
                } else {
                    state.participants.pagination = { page: 1 };
                }
            },
        );
        builder.addCase(
            addParticipant.fulfilled,
            (state: AdminParticipantState, action) => {
                if (state.participants.data) {
                    state.participants.data.push(action.payload.data);
                }
            },
        );
        builder.addCase(
            deleteParticipant.fulfilled,
            (state: AdminParticipantState, action): void => {
                if (!state.participants.data) {
                    return;
                }

                const index = state.participants.data.findIndex(
                    (participant) =>
                        participant.id === action.meta.arg.participantId,
                );
                if (index >= 0) {
                    state.participants.data.splice(index, 1);
                }
            },
        );
        builder.addCase(
            updateCriterionValues.fulfilled,
            (state: AdminParticipantState, action) => {
                if (!state.participants.data) {
                    return;
                }
                const participantId = action.meta.arg.participantId;
                const index = state.participants.data.findIndex(
                    (participant) => participant.id === participantId,
                );

                if (index >= 0) {
                    state.participants.data[index] = action.payload.data;
                }
            },
        );
        builder.addCase(
            batchDeleteParticipants.fulfilled,
            (state: AdminParticipantState, action) => {
                const deletedIds = action.meta.arg.ids;
                state.projectParticipants.data =
                    state.projectParticipants.data.filter(
                        (p) => !deletedIds.includes(p.id),
                    );
            },
        );
        builder.addCase(
            deleteAllParticipants.fulfilled,
            (state: AdminParticipantState) => {
                state.projectParticipants.data = [];
            },
        );

        builder.addCase(
            importParticipants.pending,
            (state: AdminParticipantState) => {
                resetImport(state);
                state.import.state = ImportStates.Pending;
            },
        );
        builder.addCase(
            importParticipants.fulfilled,
            (state: AdminParticipantState, action) => {
                state.import.state = ImportStates.Preview;
                if (action.payload.data) {
                    state.import.importId = action.payload.data.importId;
                    state.import.preview = action.payload.data.preview;
                }
            },
        );
        builder.addCase(
            importParticipants.rejected,
            (state: AdminParticipantState) => {
                resetImport(state);
                state.import.state = ImportStates.Fail;
            },
        );
        builder.addCase(
            confirmImport.pending,
            (state: AdminParticipantState) => {
                state.import.state = ImportStates.Pending;
            },
        );
        builder.addCase(
            confirmImport.fulfilled,
            (state: AdminParticipantState, action) => {
                state.import.state = ImportStates.Importing;
                const data = action.payload.data as ImportProcess;
                state.import.process = data;
                if (data?.status === 'completed') {
                    state.import.state = ImportStates.Complete;
                }
            },
        );
        builder.addCase(
            confirmImport.rejected,
            (state: AdminParticipantState) => {
                resetImport(state);
            },
        );
        builder.addCase(
            cancelImport.pending,
            (state: AdminParticipantState) => {
                state.import.state = ImportStates.Idle;
                state.import.importId = null;
            },
        );
        builder.addCase(
            getImportProgress.fulfilled,
            (state: AdminParticipantState, action) => {
                const data = action.payload.data as ImportProcess;

                state.import.process = data;
                if (data?.status === 'completed') {
                    state.import.state = ImportStates.Complete;
                }
            },
        );
        builder.addCase(
            getImportProgress.rejected,
            (state: AdminParticipantState) => {
                resetImport(state);
            },
        );

        builder.addCase(
            getProjectParticipants.pending,
            (state: AdminParticipantState) => {
                state.projectParticipants.loading = true;
            },
        );
        builder.addCase(
            getProjectParticipants.fulfilled,
            (state: AdminParticipantState, action) => {
                state.projectParticipants.data = action.payload.data ?? [];
                state.projectParticipants.loading = false;
                const payload = action.payload as { pagination?: unknown };
                if (payload.pagination) {
                    state.projectParticipants.pagination =
                        payload.pagination as {
                            page?: number;
                            nextPage?: number;
                            prevPage?: number;
                            pageCount?: number;
                        };
                } else {
                    state.projectParticipants.pagination = { page: 1 };
                }
            },
        );
        builder.addCase(
            getProjectParticipants.rejected,
            (state: AdminParticipantState) => {
                state.projectParticipants.loading = false;
            },
        );

        builder.addCase(LOGOUT, () => initialState);

        builder.addMatcher(
            isAnyOf(
                getParticipants.pending,
                addParticipant.pending,
                deleteParticipant.pending,
            ),
            (state: AdminParticipantState) => {
                state.participants.loading = true;
            },
        );
        builder.addMatcher(
            isAnyOf(
                getParticipants.fulfilled,
                getParticipants.rejected,
                addParticipant.fulfilled,
                addParticipant.rejected,
                deleteParticipant.fulfilled,
                deleteParticipant.rejected,
            ),
            (state: AdminParticipantState) => {
                state.participants.loading = false;
            },
        );

        builder.addMatcher(
            isAnyOf(getImportProgress.rejected, importParticipants.rejected),
            (state: AdminParticipantState) => {
                state.import.state = ImportStates.Idle;
            },
        );
    },
});

const state = (state: AppState) => state.admin.participants;

const AdminParticipantSlice = {
    reducer: slice.reducer,
    actions: {
        getParticipants,
        addParticipant,
        deleteParticipant,
        batchDeleteParticipants,
        deleteAllParticipants,
        updateCriterionValues,
        importParticipants,
        confirmImport,
        cancelImport,
        getImportProgress,
        exportParticipants,
        getImportTemplate,
        getProjectParticipants,
        ...slice.actions,
    },
    state: {
        getAll: createSelector(state, (s: AdminParticipantState) => s),
        getParticipants: createSelector(
            state,
            (s: AdminParticipantState) => s.participants,
        ),
        getProjectParticipants: createSelector(
            state,
            (s: AdminParticipantState) => s.projectParticipants,
        ),
        import: createSelector(state, (s: AdminParticipantState) => s.import),
    },
};

export default AdminParticipantSlice;
